import {
  PayloadAction,
  createAsyncThunk,
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import * as service from '../services/companyUsers';
import * as adminService from '../services/admin';
import { CountryKeys, EmptyString, Nullable } from '../types';
import { APIApplication } from '../types/application';
import { APIBuyerCreditCheck } from '../types/buyerCreditCheck';
import { APIInsuranceRequest } from '../types/insurance';
import { Company, InternalPipelineStatus, PipelineStatus, ProductStatus } from '../types/companyUser';
import { APILLC } from '../types/llc';
import { APIFXRequest } from '../types/fxRequest';
import { backendCountryValues } from '../constants/data';
import { FactoringRequest, FactoringRequestStatus } from '../types/factoring';
import { CompanyUser } from '../types/companyUser';
import { RootState } from '.';
import { DASHBOARD, SELECTED_COMPANY_ID_STORAGE_KEY } from '../constants/auth';

type Status = 'loading' | 'idle' | 'failed';

export type CompanyUserState = {
  applications: APIApplication[];
  status: Status;
  error: Nullable<string>;
  buyerCreditChecks: APIBuyerCreditCheck[];
  insuranceRequests: APIInsuranceRequest[];
  fxRequests: APIFXRequest[];
  llcRequest: Nullable<APILLC>;
  companyUser: CompanyUser;
  isFactoringActive: boolean;
};

const companyUserInitialState: Partial<CompanyUserState> = {
  applications: [],
  error: null,
  buyerCreditChecks: [],
  insuranceRequests: [],
  fxRequests: [],
  llcRequest: null,
  isFactoringActive: false,
};

export const getCountryKey = (country: string): CountryKeys => {
  // @ts-ignore
  return Object.keys(backendCountryValues).find((key) => backendCountryValues[key] === country);
};

export const companyUserSlice = createSlice({
  name: 'companyUser',
  initialState: companyUserInitialState,
  reducers: {
    resetCompanyUserState: () => companyUserInitialState,
    setNotificationPreferences: (state, action: PayloadAction<any>) => {
      if (state.companyUser)
        state.companyUser.notificationPreferences = {
          ...state.companyUser.notificationPreferences,
          ...action.payload,
        };
    },
    setFactoringActivity: (state, action) => {
      state.isFactoringActive = action.payload;
    },
    setProductStatus: (state, action: PayloadAction<Partial<ProductStatus>>) => {
      if (state.companyUser)
        state.companyUser.selectedCompany.productStatus = {
          ...state.companyUser.selectedCompany.productStatus,
          ...action.payload,
        };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCompanyUser.fulfilled, (state, action) => {
        state.companyUser = action.payload;
      })
      .addCase(updateCompanyUser.fulfilled, (state, action) => {
        state.companyUser = action.payload;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        const companyId = action.payload.company.id;
        const companyIndex = state.companyUser!.companies.findIndex((company) => company.id === companyId);
        state.companyUser!.companies[companyIndex] = action.payload.company;
        if (action.payload.company.isDraft) {
          state.companyUser!.draftCompany = action.payload.company;
        } else {
          state.companyUser!.selectedCompany = action.payload.selectedCompany!;
        }
      })
      .addCase(adminGetCompanyUserBuyerCreditChecks.fulfilled, (state, action) => {
        state.buyerCreditChecks = action.payload;
      })
      .addCase(adminGetCompanyUserInsuranceRequests.fulfilled, (state, action) => {
        state.insuranceRequests = action.payload;
      })
      .addCase(adminGetCompanyUserFXRequests.fulfilled, (state, action) => {
        state.fxRequests = action.payload;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.companyUser!.draftCompany = action.payload.company;
        state.companyUser!.selectedCompany = action.payload.selectedCompany;
        state.companyUser?.companies.push(action.payload.company);
      })
      .addCase(completeOnboarding.fulfilled, (state, action) => {
        const companyId = action.payload.company.id;
        const companyIndex = state.companyUser!.companies.findIndex((company) => company.id === companyId);
        if (companyIndex !== -1) {
          state.companyUser!.companies[companyIndex] = action.payload.company;
        } else {
          state.companyUser!.companies.push(action.payload.company);
        }
        state.companyUser!.draftCompany = null;
        state.companyUser!.selectedCompany = action.payload.selectedCompany;
        state.companyUser!.selectedCompanyRole = state.companyUser!.companyUserRoles.find(
          (role) => role.company.id === action.payload.selectedCompany.id
        )?.role;
      })
      .addMatcher(
        isFulfilled(
          loadCompanyUser,
          updateCompanyUser,
          updateCompany,
          adminGetCompanyUserBuyerCreditChecks,
          adminGetCompanyUserInsuranceRequests,
          adminGetCompanyUserFXRequests,
          createCompany,
          completeOnboarding
        ),
        (state) => {
          state.status = 'idle';
        }
      )
      .addMatcher(
        isPending(
          loadCompanyUser,
          updateCompanyUser,
          updateCompany,
          adminGetCompanyUserBuyerCreditChecks,
          adminGetCompanyUserInsuranceRequests,
          adminGetCompanyUserFXRequests,
          createCompany,
          completeOnboarding
        ),
        (state) => {
          state.status = 'loading';
          state.error = null;
        }
      )
      .addMatcher(
        isRejected(
          loadCompanyUser,
          updateCompanyUser,
          updateCompany,
          adminGetCompanyUserBuyerCreditChecks,
          adminGetCompanyUserInsuranceRequests,
          adminGetCompanyUserFXRequests,
          createCompany,
          completeOnboarding
        ),
        (state, action) => {
          state.status = 'failed';
          state.error = action.error.message || null;
        }
      );
  },
});

const factoringStatusToCompanyUserPipelineStatus: Partial<Record<FactoringRequestStatus, PipelineStatus>> = {
  created: 'application_sent',
  submitted: 'application_received',
  pending_review: 'application_received',
  termsheet: 'termsheet_sent',
  document_validation: 'termsheet_received_and_document_collection',
  due_dilligence: 'due_dilligence',
  credit_committee: 'credit_committee',
  legal: 'legal',
  onboarding: 'onboarding',
};

export const setSelectedCompany = (companyId: string) => {
  localStorage.setItem(SELECTED_COMPANY_ID_STORAGE_KEY, companyId);
  // Do not reload the dashboard page with companyId
  // because it will set that company id instead of the selected one
  if (window.location.pathname === `${DASHBOARD}/`) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('companyId');
    const queryParams = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const newUrl = `${DASHBOARD}${queryParams}`;
    window.history.replaceState({}, '', newUrl);
  }
  // Reload the page
  window.location.reload();
};

// TODO: Should we assume it is closed? TBR(To Be Refactored)
export function getPipelineStatus(factoringRequest: Nullable<FactoringRequest>): EmptyString<PipelineStatus> {
  if (factoringRequest === null || factoringRequest === null) return '';
  return factoringStatusToCompanyUserPipelineStatus[factoringRequest.status] || 'closed';
}

export function getInternalPipelineStatus(
  factoringRequest: Nullable<FactoringRequest>
): InternalPipelineStatus {
  if (factoringRequest && factoringRequest.status === 'credit_committee' && factoringRequest.creditCommittee)
    return 'credit_committee_advanced';
  return getPipelineStatus(factoringRequest) as InternalPipelineStatus;
}

export async function loadSelectedCompany(selectedCompany: Company): Promise<Company> {
  const loadedSelectedCompany = { ...selectedCompany };
  const factoringRequest = await service.getCompanyUserActiveFactoringRequest(loadedSelectedCompany.id);
  loadedSelectedCompany.factoringRequest = factoringRequest;
  loadedSelectedCompany.internalPipelineStatus = loadedSelectedCompany.productStatus.readyForFactoring
    ? 'closed'
    : getInternalPipelineStatus(factoringRequest);
  return loadedSelectedCompany;
}

export const loadCompanyUser = createAsyncThunk('companyUser/me', async () => {
  const companyUser = await service.currentUserDetail();
  // convert country from the long form to the short one (e.g. from "mexico" to "mx")
  companyUser.companies = companyUser.companies.map((company) => ({
    ...company,
    country: getCountryKey(company.country),
  }));
  if (companyUser.draftCompany?.country) {
    companyUser.draftCompany = {
      ...companyUser.draftCompany,
      country: getCountryKey(companyUser.draftCompany.country),
    };
  }
  // By default, the selected company is the first one, but the user can change it.
  const selectedCompanyId = localStorage.getItem(SELECTED_COMPANY_ID_STORAGE_KEY);
  const selectedCompany =
    selectedCompanyId && companyUser.companies.find((company) => company.id === parseInt(selectedCompanyId));
  if (companyUser.companies.length) {
    companyUser.selectedCompany = await loadSelectedCompany(selectedCompany || companyUser.companies[0]);
    companyUser.selectedCompanyRole = companyUser.companyUserRoles.find(
      (role) => role.company.id === companyUser.selectedCompany.id
    )?.role;
  }
  return companyUser;
});

export const updateCompanyUser = createAsyncThunk<
  CompanyUser,
  { user: { firstName: string; lastName: string }; contactPhone: string },
  { state: RootState }
>(
  'companyUser/update',
  async (payload: { user: { firstName: string; lastName: string }; contactPhone: string }, { getState }) => {
    const state = getState();
    const selectedCompany = state.companyUser.companyUser?.selectedCompany;
    const companyUser = await service.updateCompanyUser(payload);
    if (selectedCompany) {
      companyUser.selectedCompany = selectedCompany;
      companyUser.selectedCompanyRole = companyUser.companyUserRoles.find(
        (role) => role.company.id === selectedCompany.id
      )?.role;
    }
    // convert country from the long form to the short one (e.g. from "mexico" to "mx")
    companyUser.companies = companyUser.companies.map((company) => ({
      ...company,
      country: getCountryKey(company.country),
    }));
    return companyUser;
  }
);

export const updateCompany = createAsyncThunk(
  'companyUser/updateCompany',
  async ({ companyId, payload }: { companyId: number; payload: any }) => {
    const company = await service.updateCompany(companyId, payload);
    company.country = getCountryKey(company.country);
    // If the company is not draft, load the selected company fields
    if (!company.isDraft) {
      const selectedCompany = await loadSelectedCompany(company);
      return { company, selectedCompany };
    }
    return { company };
  }
);

// TODO: Check if selectedCompany goes here. I don't think it does. Because now, there is no company, we have just a draft company.
export const createCompany = createAsyncThunk(
  'company/create',
  async (params: { hubspotCompanyId: string | null; referralId: string | null }) => {
    const company = await service.createCompany(params);
    company.country = getCountryKey(company.country);
    const selectedCompany = await loadSelectedCompany(company);
    localStorage.setItem(SELECTED_COMPANY_ID_STORAGE_KEY, company.id.toString());
    return { company, selectedCompany };
  }
);

export const completeOnboarding = createAsyncThunk(
  'companyUser/completeOnboarding',
  async (companyId: number, { rejectWithValue }) => {
    try {
      const company = await service.completeOnboarding(companyId);
      company.country = getCountryKey(company.country);
      const selectedCompany = await loadSelectedCompany(company);
      return { company, selectedCompany };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

// The discarded draft can be the stored selected company, so the user is reloaded to pick a valid one.
export const discardDraftCompany = createAsyncThunk(
  'companyUser/discardDraftCompany',
  async (companyId: number, { dispatch, rejectWithValue }) => {
    try {
      await service.discardDraftCompany(companyId);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
    if (localStorage.getItem(SELECTED_COMPANY_ID_STORAGE_KEY) === companyId.toString()) {
      localStorage.removeItem(SELECTED_COMPANY_ID_STORAGE_KEY);
    }
    await dispatch(loadCompanyUser()).unwrap();
  }
);

// Admin data
export const adminGetCompanyUserBuyerCreditChecks = createAsyncThunk(
  'companyUser/getBuyerCreditChecks',
  async (id: number) => {
    return await adminService.adminGetBuyerCreditCheckByCompanyUserId(id);
  }
);

export const adminGetCompanyUserInsuranceRequests = createAsyncThunk(
  'companyUser/getInsuranceRequest',
  async (id: number) => {
    return await adminService.adminGetInsuranceRequestByCompanyUserId(id);
  }
);

export const adminGetCompanyUserFXRequests = createAsyncThunk(
  'companyUser/getCompanyUserFXRequest',
  async (id: number) => {
    return await adminService.adminGetFXRequestByCompanyUserId(id);
  }
);

export const { resetCompanyUserState, setNotificationPreferences, setFactoringActivity, setProductStatus } =
  companyUserSlice.actions;
